import type { Ref } from "vue";
import type { AddressDto, AOrderLine, MultiShipDto } from "../types";

/**
 * Composable that provides additional data that the basket needs for multi ship
 */
export function useMultiShip ( lines: Ref<AOrderLine[] | undefined>, config: Ref<MultiShipDto | undefined> ) {
  /**
   * A list of multi-ship lines that are valid or invalid
   */
  const multiShipValidation = ref<Record<string, boolean>>( {} );

  /**
   * Whether all multi-ship lines are valid
   */
  const isMultiShipValid = computed<boolean>( () => {
    const values = Object.values( multiShipValidation.value );
    return values.every( i => i );
  } );

  /**
   * Validate a multi-ship line
   * @param lineId The ID of the order line being validated
   * @param requireAddress Is the address required
   * @param requirePerson Is the person required
   */
  function validateMultiShip ( lineId: string, requireAddress: boolean, requirePerson: boolean ) {
    const orderLine = lines.value?.find( l => l.id === lineId );

    if ( !orderLine ) {
      return;
    }

    let isValid = requireAddress ? !!orderLine.deliveryAddressId : true;
    isValid = isValid && ( requirePerson ? !!orderLine.deliveryPersonId : true );

    multiShipValidation.value[lineId] = isValid;
  }

  /**
   * Generate a dictionary of product IDs against whether they can be split
   * to have alternate delivery destinations. A product must have a quantity on one of its
   * lines of at least 2 to be splittable
   *
   * example response:
   *
   * {
   *  1: true,
   *  2: true,
   *  3: false,
   * }
   */
  const splittableProducts = computed<Record<string, boolean>>( () => {
    if ( !lines.value ) {
      return {};
    }

    const hasAddress: Record<string, boolean> = {};

    return lines.value.reduce( ( acc, curr ) => {
      const productId = String( curr.product?.id || "" );

      if ( !Object.keys( acc ).includes( productId ) ) {
        // Initialise the object property with a value of false if it does not exist
        acc[productId] = false;
        hasAddress[productId] = true;
      }

      // Record if we have a delivery address for this product on this line
      hasAddress[productId] = hasAddress[productId] && !!curr.deliveryAddressId;

      // A product is splittable IF the quantity is > 1 and it has an address
      acc[productId] = ( curr.quantity || 0 ) > 1 && hasAddress[productId];

      return acc;
    }, {} as Record<string, boolean> );
  } );

  /**
   * Generate a dictionary of product IDs where each ID has an array of address
   * IDs currently linked to it. This is so we can prevent a product having multiple
   * order lines with the same address
   *
   * example response:
   *
   * {
   *  1: [x,y,z],
   *  2: [x],
   *  3: [x,z],
   * }
   */
  const productAddresses = computed<Record<number, string[]>>( () => {
    if ( !lines.value ) {
      return {};
    }

    return lines.value.reduce( ( acc, curr ) => {
      const productId = curr.product?.id || 0;

      if ( curr.deliveryAddressId ) {
        if ( !Object.keys( acc ).includes( String( productId ) ) ) {
          // Initialise the object with the current product ID as an array
          // containing the delivery address
          acc[productId] = [curr.deliveryAddressId];
        } else {
          // Add the delivery address from the current line to the object
          // with the current lines product ID
          acc[productId].push( curr.deliveryAddressId );
        }
      }
      return acc;
    }, {} as Record<number, string[]> );
  } );

  /**
   * True if at least one of the order lines has a missing delivery address
   */
  const missingAddresses = computed<boolean>( () => {
    if ( !config.value?.selectAddress ) {
      return false;
    }

    return lines.value?.some( line => ( !line.deliveryAddressId ) ) || false;
  } );

  /**
   * The default delivery address in the list of basket delivery addresses
   */
  const defaultAddress = computed<AddressDto | undefined>( () => {
    const addresses = config.value?.addresses;

    if ( !addresses ) {
      return undefined;
    }

    // Try and find the default address
    let result = addresses.find( address => address.isDefault );

    if ( result == null ) {
      // No address found so just pick the first one in the list
      [result] = addresses;
    }

    return result;
  } );

  return {
    defaultAddress,
    splittableProducts,
    productAddresses,
    missingAddresses,
    validateMultiShip,
    isMultiShipValid,
  };
}
